import styled, { css, keyframes } from "styled-components";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
  }
  to {
    opacity: 0;
  }
`;

const slideInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const slideOutDown = keyframes`
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(50px) scale(0.9);
  }
`;

// Styled Components
export const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: ${(props) =>
    props.$isClosing
      ? css`
          ${fadeOut} 0.3s ease forwards
        `
      : css`
          ${fadeIn} 0.3s ease
        `};
`;

export const ModalContent = styled.div`
  background: var(--surface, #ffffff);
  border-radius: 25px;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.25);
  max-width: 650px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  animation: ${(props) =>
    props.$isClosing
      ? css`
          ${slideOutDown} 0.3s ease forwards
        `
      : css`
          ${slideInUp} 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)
        `};
  border: 2px solid var(--accent-light);
  position: relative;
  overflow: auto;
  /* Fix za Chrome glitch pri brzom skrolu (providan/pomeren sadržaj) */
  transform: translateZ(0);
  overscroll-behavior: contain;
`;

export const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 32px;
  border-bottom: 1px solid var(--accent-light);
  background: var(--surface-light);
  border-radius: 12px 12px 0 0;
`;

export const ModalTitle = styled.h3`
  color: var(--text);
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--text-secondary);
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  transition: all 0.2s ease;

  &:hover {
    background: var(--surface-hover);
    color: var(--text);
  }
`;

export const ModalGrid = styled.div`
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export const OrderHeaderGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  padding: 24px;
  background: var(--surface-light);
  border-radius: 8px;
  border: 1px solid var(--accent-light);

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const InfoLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-secondary);
  display: block;
  margin-bottom: 4px;
`;

export const InfoValue = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: ${(props) =>
    props.$accent
      ? "var(--accent)"
      : props.$error
        ? "var(--error)"
        : "var(--text)"};

  display: -webkit-box;
  -webkit-line-clamp: 3; /* Maksimalno 3 reda */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SectionHeading = styled.h4`
  color: var(--text);
  margin: 0 0 16px 0;
  font-size: 1.125rem;
  font-weight: 600;
  border-bottom: 2px solid var(--accent);
  padding-bottom: 8px;
`;

export const CustomerGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

export const AddressSection = styled.div`
  background: var(--surface-light);
  padding: 16px;
  border-radius: 8px;
  border: 1px solid var(--accent-light);
`;

export const ItemsSection = styled.div`
  width: 100%;
`;

export const ItemsContainer = styled.div`
  border: 1px solid var(--accent-light);
  border-radius: 8px;
  overflow: hidden;
`;

export const OrderItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  background: ${(props) =>
    props.$index % 2 === 0 ? "var(--surface)" : "var(--surface-light)"};
  border-bottom: ${(props) =>
    props.$isLast ? "none" : "1px solid var(--accent-light)"};
`;

export const ItemInfo = styled.div`
  flex: 1;
`;

export const ItemTitle = styled.h5`
  color: var(--text);
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
`;

export const ItemDescription = styled.p`
  color: var(--text-secondary);
  margin: 0;
  font-size: 0.875rem;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Maksimalno 2 reda */
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.4;
`;

export const ItemPrice = styled.span`
  color: var(--accent);
  font-weight: 600;
  font-size: 1rem;
  margin-left: 16px;
`;

export const OrderSummary = styled.div`
  background: var(--surface-light);
  border: 1px solid var(--accent-light);
  border-radius: 8px;
  padding: 20px;
`;

export const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

export const SummaryLabel = styled.span`
  color: var(--text-secondary);
  font-size: 0.875rem;
  font-weight: 500;
`;

export const SummaryValue = styled.span`
  color: ${(props) =>
    props.$accent
      ? "var(--accent)"
      : props.$error
        ? "var(--error)"
        : "var(--text)"};
  font-weight: 600;
  font-size: 0.875rem;
`;

export const TotalRow = styled(SummaryRow)`
  border-top: 1px solid var(--accent-light);
  padding-top: 12px;
  margin-top: 12px;
  margin-bottom: 0;

  ${SummaryLabel} {
    font-size: 1rem;
    color: var(--text);
    font-weight: 600;
  }

  ${SummaryValue} {
    font-size: 1.125rem;
    font-weight: 700;
  }
`;

export const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px 32px;
  border-top: 1px solid var(--accent-light);
  background: var(--surface-light);
  border-radius: 0 0 12px 12px;
`;

export const Button = styled.button`
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  ${(props) =>
    props.$secondary
      ? `
    background: var(--surface);
    color: var(--text);
    border: 1px solid var(--accent-light);

    &:hover {
      background: var(--surface-hover);
    }
  `
      : `
    background: var(--accent);
    color: white;

    &:hover {
      background: var(--accent-dark);
    }
  `}

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
